import Swiper from 'swiper';

type TFunction = (...args: any[]) => any;

export const breakpoints = {
    m: 480,
    l: 760,
    xl: 1000,
    xxl: 1300,
    xxxl: 1700,
    xxxxl: 1800
};

/** Get a random interger between two intergers (inclusive). */
export function getRandomInteger (min: number, max: number): number {
    return Math.floor(Math.random() * (Math.floor(max) - Math.ceil(min) + 1)) + Math.ceil(min);
}

/** Remove all tags from a string. */
export function stripTags (text: string): string {
    return text.replace(/(<([^>]+)>)/gi, '');
}

/** Format a string to be used as an id. */
export function formatForId (text: string): string {
    return stripTags(text.toLocaleLowerCase().replace(' ', '-'));
}

/** Disable scroll on the body. */
export function disableBodyScroll (): void {
    document.body.style.overflow = 'hidden';
}

/** Enable scroll on the body. */
export function enableBodyScroll (): void {
    document.body.style.overflow = 'initial';
}

/** Query all focusable elements inside a container */
export function queryAllFocusableElements (containerElement: HTMLElement): NodeListOf<HTMLElement> {
    // Selector to match all focusable elements
    const selector = `
        a[href],
        area[href],
        input:not([disabled]),
        select:not([disabled]),
        textarea:not([disabled]),
        button:not([disabled]),
        iframe,
        object,
        embed,
        *[tabindex],
        *[contenteditable]
    `;

    // Return all matching elements
    return containerElement.querySelectorAll(selector) as NodeListOf<HTMLElement>;
}

/** Trap focus inside a container. */
export function trapFocus (containerElement: HTMLElement): void {
    // Get all focusable elements
    const focusableElements = queryAllFocusableElements(containerElement);

    // If there are no focusable elements stop here
    if (focusableElements.length === 0) {
        return;
    }

    // Get first and last focusable elements
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Add a keydown event listener on the first element
    firstElement.addEventListener('keydown', (event) => {
        // If the tab and shift keys are down while the first element is focused
        if (
            event.key === 'Tab'
            && !event.ctrlKey
            && !event.altKey
            && !event.metaKey
            && event.shiftKey
        ) {
            // Stop the default behavior
            event.preventDefault();
            // Set the focus on the last element
            lastElement.focus();
        }
    });

    // Add a keydown event listener on the last element
    lastElement.addEventListener('keydown', (event) => {
        // If the tab key is down while the last element is focused
        if (
            event.key === 'Tab'
            && !event.ctrlKey
            && !event.altKey
            && !event.metaKey
            && !event.shiftKey
        ) {
            // Stop the default behavior
            event.preventDefault();
            // Set the focus on the first element
            firstElement.focus();
        }
    });
}

/** Handle keyboard navigation inside Swiper. */
export function swiperKeyboardNavigationHandler (swiper: Swiper): void {
    // Swiper doesn't implement keyboard navigation correctly so we do it manually with this helper.
    // It ensures that whenever an element is focused inside a slide, that slide is active.

    // For each slide
    swiper.slides.forEach((slideElement, slideIndex) => {
        // Get all focusable elements inside the current slide
        const focusableElements = queryAllFocusableElements(slideElement);

        // For each focusable element
        focusableElements.forEach((focusableElement) => {
            // Add focus event listener
            focusableElement.addEventListener('focus', () => {
                // If the swiper has autoplay on
                if (swiper.autoplay?.running) {
                    // Stop it
                    swiper.autoplay.stop();
                }

                // If the focused slide is not active in Swiper
                if (swiper.activeIndex !== slideIndex) {
                    swiper.slideTo(slideIndex);
                }

                // Update the focused slide
                // Note: If slidesPerView is greater than one, the last slides will never be considered active, so we can't rely on Swiper's activeIndex to update our slides.
                swiperUpdateSlides(swiper, slideIndex);
            });
        });
    });
}

/** Update swiper slides with aria-hidden */
export function swiperUpdateSlides (swiper: Swiper, newActiveIndex: number): void {
    if (!swiper) {
        return;
    }

    // Get the current number of visible slides (will change based on breakpoint)
    const slidesPerView = Math.trunc(swiper.slidesPerViewDynamic());

    // For each slide
    swiper.slides.forEach((slide, index) => {
        // If the slide is visible
        if (index >= newActiveIndex && index < newActiveIndex + slidesPerView) {
            slide.setAttribute('aria-hidden', 'false');
        } else {
            slide.setAttribute('aria-hidden', 'true');
        }
    });
}

/** Log focused element */
export function logFocusedElement (): void {
    document.addEventListener('keydown', (event) => {
        if (
            event.key === 'Tab'
            && !event.ctrlKey
            && !event.altKey
            && !event.metaKey
        ) {
            setTimeout(() => {
                console.log(document.activeElement);
            }, 500);
        }
    });
}

/** Set a cookie. */
export function setCookie (key: string, value: string, days: number): void {
    // We calculate the max age in seconds
    const maxAge = 60 * 60 * 24 * days;

    // We set the cookie
    document.cookie = `${key}=${value}; max-age=${maxAge}; path=/;`;
}

/** Get a cookie. */
export function getCookie (key: string): string {
    // Get all cookies in an array
    const cookies = document.cookie.split("; ");

    // Find cookie using key
    const cookie = cookies.find((cookie) => cookie.startsWith(`${key}=`));

    if (cookie) {
        // Return cookie value
        return cookie.split('=')[1];
    } else {
        return null;
    }
}

/**
 * Debounce a function.
 *
 * Example usage: debounce(callback)();
 *  */
export function debounce (callback: TFunction, delay = 500): TFunction {
    let timeout;

    return function () {
        clearTimeout(timeout);
        timeout = setTimeout(callback, delay);
    };
}